import {Discussion} from "./Discussion";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {ErrorViewer, useErrorReporter} from "../general/ErrorViewer";
import {LoginButton, MenuItemEntry} from "../components/LoginButton";
import {useAuth} from "../auth/AuthHook";
import {Link, Route, Routes, useLocation, useNavigate, useParams} from "react-router-dom";
import {DocViewer} from "../docs/DocViewer";
import {CaseFilter} from "../filter/CaseFilter";
import {IntroBox} from "../general/IntroBox";
import {WelcomeBox} from "../general/WelcomeBox";
import {SlackSettings} from "../integrations/SlackSettings";
import {TeamsSettings} from "../integrations/TeamsSettings";
import {ConfluenceAdmin} from "../integrations/ConfluenceAdmin";
import {RecentCases} from "../filter/RecentCases";
import {SelectableButton, SelectableLink} from "../components/SelectableLink";
import {PlusIcon, SearchIcon} from "../components/Icons";
import {HelpViewer} from "../docs/HelpViewer";
import {AboutBox} from "../docs/AboutBox";
import {AppFooter} from "../general/AppFooter";
import {withBase} from "../utils/basePath";
import {TitleBox} from "../general/TitleBox";
import {SeparatorLine} from "../components/SeparatorLine";

export interface SupportFrameProps {
    showEmbedded: boolean
}

export function SupportFrame({showEmbedded}: SupportFrameProps) {
    const docPath = useParams()["*"];


    const {t} = useTranslation()
    const {isLoggedIn, isAdmin, isSysAdmin, userData} = useAuth()
    const reporter = useErrorReporter()
    const [initiationTimeStamp, setInitiationTimeStamp] = useState(0)
    const navigate = useNavigate()
    const location = useLocation()
    const [isAboutOpen, setIsAboutOpen] = useState(false)


    useEffect(() => {

        if (location.pathname === "/redirect" && (isLoggedIn() || location.search === "")) {
            navigate("/cases/new")
        }
        if (location.pathname === "/" && isLoggedIn()) {
            navigate("/cases/new")
        }
    }, [location.pathname, location.search, navigate, isLoggedIn])

    const handleNewCaseInitiation = useCallback(() => {
        navigate("/cases/new")
        setInitiationTimeStamp(Date.now())
    }, [navigate])


    const noOp = useCallback(() => {
    }, [])

    const handleAboutClick = useCallback(() => {
        setIsAboutOpen(true)
    }, [])

    const handleAboutClose = useCallback(() => {
        setIsAboutOpen(false)
    }, [])

    const settingsMenuItems: MenuItemEntry[] = useMemo(() => {
        const settingsGroup = []
        if (isAdmin || isSysAdmin) {
            settingsGroup.push({name: t("Slack Integration"), link: "/settings/slack"})
        }

        if (isSysAdmin) {
            settingsGroup.push({name: t("Teams Integration"), link: "/settings/teams"})
            settingsGroup.push({name: t("Confluence Admin"), link: "/settings/confluence"})
        }

        if (settingsGroup.length > 0) {
            settingsGroup.unshift({name: t("Settings"), link: undefined})
        }

        return [
            {name: t("Documentation"), link: undefined},
            {name: t("Help"), link: "/help/guide", newTab: true},
            {name: t("About"), action: handleAboutClick},
            ...settingsGroup,
        ]
    }, [handleAboutClick, isAdmin, isSysAdmin, t])


    return <>
        <div id="main-support-frame" className={`flex flex-row justify-between grow w-full h-full relative`}>
            <div id="first-column"
                 className={`flex flex-col justify-start bg-gray-light-dark overflow-y-auto h-full min-w-[20%] 2xl:min-w-[15%] max-w-[25%] 2xl:max-w-[20%]`}>
                {!showEmbedded &&
                    <div>
                        <TitleBox lineHidden={true}>
                            <img src={withBase(`/logo`)} alt="" className="w-32 mx-auto text-center font-bold  py-8" />
                        </TitleBox>
                        <SeparatorLine margin={3}/>

                    </div>
                }
                <AboutBox isOpen={isAboutOpen} onClose={handleAboutClose}/>
                <div id={"nav-buttons-area"} className={"px-2 py-6"}>
                    <div className={"flex flex-col w-full"}>
                            <SelectableButton
                                className="text-left flex-row justify-start h-auto py-1"
                                onClick={handleNewCaseInitiation}>
                                <div className={"flex flex-row gap-2 items-center text-base text-gray-300"}>
                                    <PlusIcon className={"size-4"}/>
                                    <span>{t("Create new case ...")}</span>
                                </div>
                            </SelectableButton>
                    </div>
                    <div className={"flex flex-col w-full"}>
                        <SelectableLink to={"/filter?fid=main"} className={"flex flex-row gap-2 items-center text-base text-gray-300"} isSelected={false} hasBorder={false}>
                            <SearchIcon className={"size-4"}/>
                            <span>{t("Search cases")}</span>
                        </SelectableLink>
                    </div>
                </div>
                <div className={"h-full grow"}>
                    <div className={"flex flex-col h-2/3"}>
                        <Routes>
                            <Route path="/cases/new" element={
                                <RecentCases/>
                            }/>
                            <Route path="/cases/:caseId" element={
                                <RecentCases/>
                            }/>
                            <Route path="*" element={
                                <RecentCases/>
                            }/>
                        </Routes>

                    </div>
                </div>
                <div className="justify-self-end mx-3">
                    <LoginButton itemsBefore={settingsMenuItems}/>
                </div>

            </div>
            <div id="second-column"
                 className="flex flex-col justify-between h-full bg-gray-fc-dark overflow-auto overflow-y-auto grow">
                <div id="supportframe-discussion-container"
                     className="flex flex-col items-center w-full grow overflow-auto h-full">
                    <Routes>
                        {isLoggedIn() &&
                            <>
                                <Route path="/cases/new" element={
                                    <Discussion/>
                                }/>
                                <Route path="/cases/:caseId" element={
                                    <Discussion/>
                                }/>
                                <Route path="/docs/*" element={
                                    <DocViewer doc_prefix="/docs" internal_prefix="/internal"/>
                                }/>
                                <Route path="/filter" element={
                                    <CaseFilter/>}
                                />
                                <Route path="/redirect" element={
                                    <WelcomeBox/>
                                }/>
                                <Route path="/settings/slack" element={
                                    <SlackSettings/>
                                }/>
                                <Route path="/settings/teams" element={
                                    <TeamsSettings/>
                                }/>
                                <Route path="/settings/confluence" element={
                                    <ConfluenceAdmin/>
                                }/>
                                <Route path="/help/*" element={
                                    <HelpViewer/>
                                }/>
                                <Route path="/" element={
                                    <WelcomeBox/>
                                }/>
                            </>
                        }
                        {!isLoggedIn() &&
                            <>
                                <Route path="/help/*" element={
                                    <HelpViewer/>
                                }/>
                                <Route path="*" element={
                                    <WelcomeBox/>
                                }/>
                            </>
                        }
                    </Routes>
                </div>
                {/*<footer className="flex justify-center gap-4 py-1 text-xs text-gray-500">*/}
                {/*    <a href="https://enoventum.com/privacy-policy" target="_blank" rel="noopener noreferrer">{t("Privacy Policy")}</a>*/}
                {/*    <a href="https://enoventum.com/terms-of-use" target="_blank" rel="noopener noreferrer">{t("Terms of Use")}</a>*/}
                {/*</footer>*/}
                <AppFooter/>
            </div>
            <div id="third-column" className="py-4 flex h-full overflow-y-auto flex-col justify-between bg-gray-really max-w-[25%] min-w-[10%] xl:max-w-[15%] absolute right-0 bg-transparent pointer-events-none">
                <div className="flex flex-col items-center w-full grow overflow-auto justify-between">
                    <div className={"flex flex-col overflow-y-auto w-full pointer-events-auto"}>
                        <ErrorViewer/>
                    </div>
                </div>
            </div>
        </div>
</>
}